import { useState } from "react";

export const faqs = [
  {
    question: "Do I need a passport for a Bahamas or Caribbean cruise from Tampa?",
    answer:
      "It depends on the cruise itinerary. Closed-loop cruises (departing and returning to the same US port like Tampa) may accept a government-issued photo ID and birth certificate for US citizens. However, we strongly recommend carrying a valid passport. If you miss the ship in a foreign port, get injured and need to fly home, or the itinerary changes to include a non-US port, a passport becomes mandatory. Airlines will not accept birth certificates for international flights. For peace of mind on any Tampa cruise, bring your passport.",
  },
  {
    question: "How fast can I get a passport in Clearwater, FL?",
    answer:
      "Our same-day emergency service delivers a new or renewed passport within 24–72 hours of receiving your sealed application. Rush processing takes 4–7 business days, and standard expedited service takes 2–3 weeks. For Clearwater residents with flights from Tampa International or St. Pete-Clearwater Airport, we recommend selecting your processing speed based on your departure date minus a 48-hour safety buffer.",
  },
  {
    question: "What if my passport was damaged at Clearwater Beach or on a boat?",
    answer:
      "Salt water, sand, and humidity can warp covers, blur photos, or separate laminate layers. Once a passport shows visible damage — water stains, torn pages, separated binding, or unreadable data — it is invalid for travel. You must apply for a replacement using form DS-11 (treated as a new application) and submit the damaged passport. We offer emergency damaged passport replacement with 24–72 hour turnaround for Clearwater travelers facing imminent departures.",
  },
  {
    question: "Can I fly to Toronto from Tampa without a passport?",
    answer:
      "No. Canada requires all US citizens arriving by air to present a valid passport book. Passport cards are only accepted for land and sea border crossings. If you are flying Air Canada or another carrier from Tampa International (TPA) to Toronto Pearson, a passport book is mandatory. The passport must be valid for the duration of your stay — though we recommend at least 6 months of validity to avoid airline check-in complications.",
  },
  {
    question: "What is the Schengen 3-month validity rule for European travel?",
    answer:
      "The Schengen Area (most of Western and Central Europe) requires your passport to be valid for at least 3 months beyond your planned departure date from the Schengen zone. Some countries within Schengen apply stricter interpretations. Because Tampa International offers direct flights to London, Frankfurt, and Zurich, Clearwater travelers frequently face this rule. We recommend ensuring 6+ months of validity for any European trip to eliminate risk.",
  },
  {
    question: "Can Allegiant Air at St. Pete-Clearwater (PIE) fly me internationally?",
    answer:
      "Allegiant Air at PIE operates domestic flights only. PIE has no customs and immigration facilities for international departures or arrivals. To fly internationally, Clearwater residents must use Tampa International Airport (TPA), which offers direct international flights to London, Frankfurt, Zurich, Cancun, Toronto (seasonal), and connecting service worldwide. Allegiant from PIE can connect you to domestic hubs like Fort Lauderdale for international flights, but you will clear customs at the international gateway.",
  },
  {
    question: "How far in advance should I apply for my child's passport before spring break?",
    answer:
      "Child passports (for applicants under 16) take longer than adult renewals and require both parents to appear in person at an acceptance facility — or submit a notarized DS-3053 consent form if one parent is unavailable. For a Clearwater spring break trip to Cancun, Jamaica, or the Bahamas, start the child passport process at least 10–12 weeks before departure. If you are within 4 weeks of travel, use our emergency child passport service.",
  },
  {
    question: "Can I renew an expired passport quickly if I have a cruise or flight next week?",
    answer:
      "Yes. If your most recent passport was issued within the last 15 years, was valid for 10 years, and you were 16 or older when it was issued, you qualify for DS-82 renewal by mail — even if it is expired. For emergency timelines, we process DS-82 renewals through our expedited courier channel with 24–72 hour turnaround. You do not need an acceptance facility appointment for renewals, which saves valuable time.",
  },
  {
    question: "Where can I get passport photos in Clearwater that will be accepted?",
    answer:
      "USPS locations in Clearwater, CVS, Walgreens, and FedEx Office all offer passport photo services. However, rejections are common due to improper lighting, shadows, background color issues, or facial expression problems. We recommend using a dedicated passport photo studio or our guided photo service, which reviews your photo against State Department criteria before submission. Avoid photos taken after beach days when tan lines or sunburn may affect acceptance.",
  },
  {
    question: "Can I get a same-day passport in Clearwater without driving to Miami?",
    answer:
      "Yes. Our same-day emergency service eliminates the need to drive 280 miles to the Miami Passport Agency. You complete your application online, visit a local Pinellas County acceptance facility, and overnight the sealed documents to our registered courier. We hand-carry your application through the State Department's expedited processing channel. Your new passport ships directly to your Clearwater address via FedEx within 24–72 hours.",
  },
  {
    question: "Do I need a passport for a closed-loop cruise from Tampa to the Caribbean?",
    answer:
      "US citizens on closed-loop cruises (departing and returning to the same US port) may use a government-issued photo ID plus an original or certified birth certificate. However, cruise lines strongly encourage passports. If you miss the ship in a foreign port and need to fly home, a birth certificate will not get you on a flight. If you take a shore excursion that runs late, or if the ship skips a port and changes itinerary, you may find yourself in a country that requires a passport. We recommend passports for all cruise travel.",
  },
  {
    question: "What documents do I need for a first-time passport application in Clearwater?",
    answer:
      "You need: (1) proof of US citizenship (original or certified birth certificate, naturalization certificate, or previous US passport), (2) a government-issued photo ID (driver's license or state ID), (3) one passport photo meeting State Department specs, (4) completed DS-11 form, and (5) payment for government fees. You must appear in person at an acceptance facility (USPS or Clerk of Court) to have your identity verified and documents sealed. We guide you through every document and form requirement.",
  },
];

export default function ClearwaterFAQSection() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  return (
    <section id="faq" className="py-16 md:py-20 bg-white">
      <div className="max-w-4xl mx-auto px-4 md:px-8">
        <div className="text-center mb-12">
          <span className="inline-block text-[#B22222] text-xs font-bold uppercase tracking-[0.15em] mb-3">
            FAQ
          </span>
          <h2 className="font-serif text-2xl md:text-4xl font-bold text-[#1A365D] mb-4">
            Clearwater Passport Questions Answered
          </h2>
          <p className="text-gray-600 text-sm md:text-base max-w-2xl mx-auto leading-relaxed">
            Everything Pinellas County travelers need to know about passports, cruises,
            spring training trips, and expedited processing from Tampa Bay.
          </p>
        </div>

        <div className="space-y-3">
          {faqs.map((faq, i) => (
            <div
              key={i}
              className={`rounded-lg border transition-all ${openIndex === i ? "border-[#F6C344] shadow-sm" : "border-gray-200"}`}
            >
              <button
                onClick={() => setOpenIndex(openIndex === i ? null : i)}
                className="w-full flex items-center justify-between gap-4 p-5 text-left"
              >
                <span className="font-semibold text-sm text-[#1A365D]">{faq.question}</span>
                <i
                  className={`${openIndex === i ? "ri-subtract-line" : "ri-add-line"} text-[#F6C344] text-lg shrink-0`}
                ></i>
              </button>
              {openIndex === i && (
                <div className="px-5 pb-5">
                  <p className="text-gray-600 text-sm leading-relaxed">{faq.answer}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}